import ApiService from '@/services/api'
import Sentry from '@/services/sentry'
import Vue from 'vue'

/**
 * @param {Store}          context
 * @param {Store.dispatch} context.dispatch
 * @param {Vue}            instance         - accepts global or local Vue instances.
 *
 */
const initializeApp = async ({ dispatch }, instance) => {
  await instance.prototype.$featureFlagsManager.initialize()
  const user = await dispatch('fetchUserInfo')
  Sentry.identifyUserByEmail(user.email)
  Vue.tracking.identifyUser(user, 'web')
}

/**
 * @param {Store}         context
 * @param {Store.commit}  context.commit
 * @param {Store.state}   context.state
 * @param {Store.getters} context.getters
 * @param {object[]}      permissions
 */
const setSelectedGroupId = ({ commit, state, getters }, permissions) => {
  const groups = permissions.map(permission => permission.group)

  if (groups.length > 0) {
    const selectedGroupId = getters.getSelectedGroupId(state)

    if (
      !selectedGroupId ||
      !groups.find(group => group.id === selectedGroupId)
    ) {
      commit('setSelectedGroupId', groups[0].id)
    }
  } else {
    commit('setSelectedGroupId', null)
  }
}

/**
 * @param   {Store}         context
 * @param   {Store.commit}  context.commit
 * @param   {Store.state}   context.state
 * @param   {Store.getters} context.getters
 *
 * @returns {object}
 */
const fetchUserInfo = async ({ commit, state, getters }) => {
  const response = await ApiService.fetchUserInfo()
  const user = response.data
  const permissions = user.permissions

  commit('setUser', user)
  commit('setPermissions', permissions)

  setSelectedGroupId(
    {
      commit,
      state,
      getters
    },
    permissions
  )

  return user
}

/**
 * @param   {Store}         context
 * @param   {Store.commit}  context.commit
 * @param   {Store.state}   context.state
 * @param   {Store.getters} context.getters
 * @param   {object}        payload
 *
 * @returns {object[]}
 */
const fetchSeasons = async ({ commit, state, getters }, payload) => {
  const response = await ApiService.fetchSeasons(payload.groupId)
  const seasons = response.data
  commit('setSeasons', seasons)

  if (seasons.length > 0) {
    const selectedSeasonId = getters.getSelectedSeasonId(state)

    if (
      !selectedSeasonId ||
      !seasons.find(season => season.id === selectedSeasonId)
    ) {
      commit('setSelectedSeasonId', seasons[0].id)
    }
  } else {
    commit('setSelectedSeasonId', null)
  }

  return seasons
}

/**
 * @param {Store}        context
 * @param {Store.commit} context.commit
 * @param {object}       payload
 */
const updateSelectedGroupId = ({ commit }, payload) => {
  commit('setSelectedGroupId', payload.groupId)
}

/**
 * @param {Store}        context
 * @param {Store.commit} context.commit
 * @param {object}       payload
 */
const updateSelectedSeasonId = ({ commit }, payload) => {
  commit('setSelectedSeasonId', payload.seasonId)
}

/**
 * @param   {Store}        context
 * @param   {Store.commit} context.commit
 * @param   {object}       payload
 *
 * @returns {object[]}
 */
const fetchInvites = async ({ commit }, payload) => {
  const response = await ApiService.fetchInvites(payload.groupId)
  const invites = response.data

  commit('setInvites', invites)

  return invites
}

/**
 * @param   {Store}        context
 * @param   {Store.commit} context.commit
 * @param   {object}       payload
 *
 * @returns {object[]}
 */
const createInvite = async ({ commit }, payload) => {
  const response = await ApiService.createInvite(
    payload.inviteeEmail,
    payload.groupId,
    payload.role
  )
  const invite = response.data

  commit('addInvite', invite)

  return invite
}

/**
 * @param {Store}        context
 * @param {Store.commit} context.commit
 * @param {object}       payload
 */
const deleteInvite = async ({ commit }, payload) => {
  await ApiService.deleteInvite(payload.inviteId)

  commit('deleteInvite', payload.inviteId)
}

/**
 * @type {object}
 */
const actions = {
  initializeApp,
  fetchUserInfo,
  fetchSeasons,
  updateSelectedGroupId,
  updateSelectedSeasonId,
  fetchInvites,
  createInvite,
  deleteInvite
}

export default actions
